import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Chip,
  TextField,
  MenuItem,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack
} from '@mui/material';
import DataTable from '../../../components/design/DataTable';
import LoadingState from '../../../components/design/LoadingState';
import EmptyState from '../../../components/design/EmptyState';
import externalApiService from '../../../services/externalApiService';

function formatDate(v) {
  if (!v) return '—';
  try { return new Date(v).toLocaleString(); } catch { return String(v); }
}

function statusChip(status) {
  const s = String(status || '');
  const color =
    s === 'read' ? 'success' :
    s === 'delivered' ? 'success' :
    s === 'sent' ? 'success' :
    s === 'failed' ? 'error' :
    s === 'processing' ? 'warning' :
    s === 'canceled' ? 'default' :
    'default';
  const label =
    s === 'read' ? 'Lida' :
    s === 'delivered' ? 'Entregue' :
    s === 'sent' ? 'Enviado' :
    s === 'failed' ? 'Falhou' :
    s === 'processing' ? 'Processando' :
    s === 'canceled' ? 'Cancelado' :
    s === 'pending' ? 'Pendente' :
    'Recebido';
  return <Chip size="small" color={color} label={label} />;
}

export default function ExternalApiSendsPage() {
  const [loading, setLoading] = useState(false);
  const [integrations, setIntegrations] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [filters, setFilters] = useState({ status: '', integrationId: '', serviceChannel: '', messageCategory: '', messageTemplateId: '', number: '', start: '', end: '' });
  const [page, setPage] = useState(1);
  const [limit] = useState(50);
  const [total, setTotal] = useState(0);
  const [rows, setRows] = useState([]);
  const [details, setDetails] = useState({ open: false, data: null });

  const loadIntegrations = useCallback(async () => {
    try {
      const resp = await externalApiService.listIntegrations();
      const data = resp?.data?.data || resp?.data || [];
      const list = Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : [];
      setIntegrations(list);
    } catch {
      setIntegrations([]);
    }
  }, []);

  const loadTemplates = useCallback(async () => {
    try {
      const resp = await externalApiService.listMessageTemplates();
      const data = resp?.data?.data || resp?.data || [];
      const list = Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : [];
      setTemplates(list);
    } catch {
      setTemplates([]);
    }
  }, []);

  const load = useCallback(async (nextPage = 1) => {
    setLoading(true);
    try {
      const resp = await externalApiService.listMessages({
        page: nextPage,
        limit,
        status: filters.status || undefined,
        integrationId: filters.integrationId || undefined,
        serviceChannel: filters.serviceChannel || undefined,
        messageCategory: filters.messageCategory || undefined,
        messageTemplateId: filters.messageTemplateId || undefined,
        number: filters.number || undefined,
        start: filters.start || undefined,
        end: filters.end || undefined
      });
      const payload = resp?.data?.data || resp?.data;
      const data = payload?.data || payload;
      const outRows = data?.rows || [];
      const outCount = data?.count || 0;
      setRows(Array.isArray(outRows) ? outRows : []);
      setTotal(Number(outCount) || 0);
      setPage(nextPage);
    } catch {
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [filters, limit]);

  useEffect(() => { loadIntegrations().catch(() => {}); }, [loadIntegrations]);
  useEffect(() => { loadTemplates().catch(() => {}); }, [loadTemplates]);
  useEffect(() => { load(1).catch(() => {}); }, [load]);

  const applyFilters = async () => {
    await load(1);
  };

  const openDetails = async (id) => {
    setLoading(true);
    try {
      const resp = await externalApiService.getMessage(id);
      const data = resp?.data?.data || resp?.data;
      setDetails({ open: true, data: data?.data || data });
    } catch {
      setDetails({ open: true, data: null });
    } finally {
      setLoading(false);
    }
  };

  const closeDetails = () => setDetails({ open: false, data: null });
  const canReprocess = (() => {
    const row = details?.data?.row || null;
    return row && String(row.status) === 'failed';
  })();

  const reprocess = async () => {
    const row = details?.data?.row || null;
    if (!row?.id) return;
    setLoading(true);
    try {
      await externalApiService.reprocessMessage(row.id);
      await openDetails(row.id);
      await load(page);
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    { key: 'createdAt', label: 'Data/Hora', render: (v) => formatDate(v) },
    { key: 'destinationNumber', label: 'Destino' },
    { key: 'integration', label: 'Integração', render: (_, r) => r.integration?.name || '—' },
    { key: 'serviceChannel', label: 'Serviço', render: (v) => v === 'sms' ? 'SMS' : 'WhatsApp' },
    {
      key: 'messageCategory',
      label: 'Tipo',
      render: (v) => v === 'billing' ? 'Cobrança' : v === 'otp' ? 'OTP' : 'Serviço'
    },
    { key: 'messageTemplate', label: 'Mensagem', render: (_, r) => r.messageTemplate?.name || 'Manual' },
    {
      key: 'messageContent',
      label: 'Conteúdo',
      render: (v) => String(v || '').length > 60 ? `${String(v).slice(0, 60)}…` : String(v || '')
    },
    { key: 'selectedConnection', label: 'Conexão', render: (_, r) => r.selectedConnection?.name || '—' },
    { key: 'providerType', label: 'Provider' },
    { key: 'status', label: 'Status', render: (v) => statusChip(v) },
    { key: 'attempts', label: 'Tentativas', align: 'right' },
    { key: 'errorMessage', label: 'Erro', render: (v) => v ? String(v) : '—' },
    {
      key: 'actions',
      label: '',
      align: 'right',
      render: (_, r) => (
        <Button size="small" variant="outlined" onClick={() => openDetails(r.id)}>Detalhes</Button>
      )
    }
  ];

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <Box p={3}>
      <Typography variant="h5" mb={2}>Envios ERP</Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box display="grid" gridTemplateColumns="repeat(4, 1fr)" gap={2}>
          <TextField
            select
            label="Status"
            value={filters.status}
            onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
            size="small"
          >
            <MenuItem value="">Todos</MenuItem>
            <MenuItem value="received">Recebido</MenuItem>
            <MenuItem value="pending">Pendente</MenuItem>
            <MenuItem value="processing">Processando</MenuItem>
            <MenuItem value="sent">Enviado</MenuItem>
            <MenuItem value="delivered">Entregue</MenuItem>
            <MenuItem value="read">Lida</MenuItem>
            <MenuItem value="failed">Falhou</MenuItem>
            <MenuItem value="canceled">Cancelado</MenuItem>
          </TextField>
          <TextField
            select
            label="Integração"
            value={filters.integrationId}
            onChange={(e) => setFilters(prev => ({ ...prev, integrationId: e.target.value }))}
            size="small"
          >
            <MenuItem value="">Todas</MenuItem>
            {integrations.map(i => (
              <MenuItem key={i.id} value={String(i.id)}>{i.name}</MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Serviço"
            value={filters.serviceChannel}
            onChange={(e) => setFilters(prev => ({ ...prev, serviceChannel: e.target.value }))}
            size="small"
          >
            <MenuItem value="">Todos</MenuItem>
            <MenuItem value="whatsapp">WhatsApp</MenuItem>
            <MenuItem value="sms">SMS</MenuItem>
          </TextField>
          <TextField
            select
            label="Tipo"
            value={filters.messageCategory}
            onChange={(e) => setFilters(prev => ({ ...prev, messageCategory: e.target.value }))}
            size="small"
          >
            <MenuItem value="">Todos</MenuItem>
            <MenuItem value="billing">Cobrança</MenuItem>
            <MenuItem value="otp">OTP</MenuItem>
            <MenuItem value="service">Serviço</MenuItem>
          </TextField>
        </Box>
        <Box display="grid" gridTemplateColumns="repeat(4, 1fr)" gap={2} mt={2}>
          <TextField
            select
            label="Mensagem"
            value={filters.messageTemplateId}
            onChange={(e) => setFilters(prev => ({ ...prev, messageTemplateId: e.target.value }))}
            size="small"
          >
            <MenuItem value="">Todas</MenuItem>
            {templates.map(template => (
              <MenuItem key={template.id} value={String(template.id)}>{template.name}</MenuItem>
            ))}
          </TextField>
          <TextField
            label="Número"
            value={filters.number}
            onChange={(e) => setFilters(prev => ({ ...prev, number: e.target.value }))}
            size="small"
          />
          <TextField
            label="Início"
            type="datetime-local"
            value={filters.start}
            onChange={(e) => setFilters(prev => ({ ...prev, start: e.target.value }))}
            size="small"
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Fim"
            type="datetime-local"
            value={filters.end}
            onChange={(e) => setFilters(prev => ({ ...prev, end: e.target.value }))}
            size="small"
            InputLabelProps={{ shrink: true }}
          />
        </Box>
        <Box mt={2} display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="body2" color="text.secondary">
            {total ? `${total} registros` : '—'}
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" onClick={() => load(1)}>Recarregar</Button>
            <Button variant="contained" onClick={applyFilters}>Aplicar filtros</Button>
          </Stack>
        </Box>
      </Paper>

      {loading ? (
        <LoadingState label="Carregando envios..." />
      ) : rows.length ? (
        <>
          <DataTable columns={columns} rows={rows} getKey={(r) => r.id} />
          <Box display="flex" justifyContent="space-between" alignItems="center" mt={2}>
            <Button variant="outlined" disabled={page <= 1} onClick={() => load(page - 1)}>Anterior</Button>
            <Typography variant="body2" color="text.secondary">
              Página {page} de {totalPages}
            </Typography>
            <Button variant="outlined" disabled={page >= totalPages} onClick={() => load(page + 1)}>Próxima</Button>
          </Box>
        </>
      ) : (
        <Paper>
          <EmptyState title="Nenhum envio encontrado" />
        </Paper>
      )}

      <Dialog open={details.open} onClose={closeDetails} maxWidth="md" fullWidth>
        <DialogTitle>Detalhes do envio</DialogTitle>
        <DialogContent>
          {details.data ? (
            <Box display="grid" gap={2}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Registro</Typography>
                <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                  {JSON.stringify(details.data.row || details.data, null, 2)}
                </pre>
              </Paper>
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Histórico de status</Typography>
                <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                  {JSON.stringify(details.data.history || [], null, 2)}
                </pre>
              </Paper>
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Logs recentes da integração</Typography>
                <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                  {JSON.stringify(details.data.logs || [], null, 2)}
                </pre>
              </Paper>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">Falha ao carregar detalhes.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={reprocess} disabled={!canReprocess || loading}>Reprocessar</Button>
          <Button onClick={closeDetails}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
